/**
 * Wrapin - Gift Wrap: app hook, add-to-cart flow, gift note, and image zoom.
 *
 * After theme:cart:refresh, the cart web component runs getCart() → build() → enableCartButtons().
 * That re-enables EVERY <button> inside the cart unless it has data-remain-disabled.
 * So we set disabled + data-remain-disabled together, and re-sync from /cart.js on theme:cart:change.
 *
 * The app injects its HTML into #ptw-wrapin and the cart drawer re-renders on refresh,
 * so all click handling is delegated from document.
 *
 * Loaded as a module: strict mode and module scope, so only the app hook is global.
 */
const ROOT = window.Shopify?.routes?.root ?? '/';
const WRAPIN_VENDOR = 'wrapin';
const ADD_BUTTON_ID = 'ptw-add-gift-pro-ck';

let syncTimer;

const refreshCart = () => document.dispatchEvent(new CustomEvent('theme:cart:refresh'));

/**
 * GET (no body) or POST JSON to a Shopify AJAX cart endpoint.
 * @param {string} path - e.g. 'cart.js'
 * @param {object} [body]
 */
const cartRequest = async (path, body) => {
  const options = { headers: { Accept: 'application/json' } };
  if (body !== undefined) {
    options.method = 'POST';
    options.headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  }
  const response = await fetch(`${ROOT}${path}`, options);
  if (!response.ok) {
    throw new Error(`${path} failed: ${response.status}`);
  }
  return response.json();
};

const cartHasWrapin = (cart) =>
  (cart?.items ?? []).some((item) => (item.vendor ?? '').toLowerCase() === WRAPIN_VENDOR);

const setButtonLocked = (button, locked) => {
  if (!button) {
    return;
  }
  button.disabled = locked;
  button.setAttribute('aria-disabled', String(locked));
  // Theme enableCartButtons() skips buttons with data-remain-disabled after a cart refresh.
  button.toggleAttribute('data-remain-disabled', locked);
};

const scheduleButtonSync = () => {
  clearTimeout(syncTimer);
  syncTimer = setTimeout(async () => {
    try {
      const cart = await cartRequest('cart.js');
      setButtonLocked(document.getElementById(ADD_BUTTON_ID), cartHasWrapin(cart));
    } catch (error) {
      console.warn('Gift wrap cart sync failed', error);
    }
  }, 100);
};

/** Save the gift note to the cart note, and clear the legacy gift-note attribute. */
const updateCartNote = async (note = '') => {
  const cart = await cartRequest('cart/update.js', {
    note,
    attributes: { 'gift-note': '' }
  });
  if (cart.note !== note) {
    console.warn(`Note mismatch: expected "${note}", got "${cart.note}"`);
  }
  refreshCart();
  return cart;
};

/**
 * Called by the Wrapin app after it injects its HTML. It must be a global.
 * Moves the image into the label and restyles the add button as a theme button.
 */
window.ptwAfterWrapinLoadHook = () => {
  const image = document.querySelector('#ptw-wrapin .ptw-gift-image');
  const label = document.querySelector('#ptw-wrapin .ptw-app-label label');
  if (image && label) {
    label.prepend(image);
  }

  const button = document.getElementById('ptw-add-gift-pro');
  if (button) {
    button.id = ADD_BUTTON_ID;
    button.type = 'button';
    button.classList.remove('button');
    button.classList.add('btn', 'btn--primary', 'btn--solid');
  }

  scheduleButtonSync();
};

/** Add the gift wrap variant, then mark the cart as gift-wrapped (no note). */
const addGiftWrap = async (button) => {
  if (button.disabled) {
    return;
  }
  const variantId = document.getElementById('ptw-variant-id')?.value;
  if (!variantId) {
    return;
  }

  setButtonLocked(button, true);

  try {
    await cartRequest('cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] });
  } catch (error) {
    console.error('Gift wrap add failed', error);
    setButtonLocked(button, false);
    return;
  }

  try {
    await cartRequest('cart/update.js', { attributes: { 'gift-wrapped': 'true' } });
  } catch (error) {
    console.warn('Gift wrap attribute update failed', error);
  }
  refreshCart();
};

/** Show a text from a data attribute pair, e.g. data-label-add / data-label-update. */
const setNoteText = (element, prefix, hasNote) => {
  element.textContent = element.dataset[hasNote ? `${prefix}Update` : `${prefix}Add`];
};

/**
 * Save the gift note from #gift-note, with button feedback.
 * Wording depends on whether the cart had a note before (data-saved-note on the textarea).
 */
const saveGiftNote = async (button) => {
  const originalText = button.textContent;
  const textarea = document.getElementById('gift-note');
  const note = textarea?.value.trim() ?? '';
  const hadNote = Boolean(textarea?.dataset.savedNote?.trim());
  const status = button.closest('.cart__widget__content__inner')?.querySelector('[data-gift-note-status]');

  button.disabled = true;
  button.textContent = 'Adding...';

  try {
    await updateCartNote(note);
    if (textarea) {
      textarea.dataset.savedNote = note;
    }
    button.textContent = hadNote ? 'Gift Note Updated!' : 'Gift Note Added!';
    document.querySelectorAll('[data-gift-note-label]').forEach((label) => setNoteText(label, 'label', Boolean(note)));

    if (status && note) {
      status.textContent = hadNote ? status.dataset.statusUpdated : status.dataset.statusAdded;
      status.hidden = false;
      // Next frame so the opacity transition runs after unhiding
      requestAnimationFrame(() => status.classList.add('is-visible'));
    }

    setTimeout(() => {
      setNoteText(button, 'cta', Boolean(note));
      button.disabled = false;
      if (status) {
        status.classList.remove('is-visible');
        // Wait for the fade-out before hiding the message
        setTimeout(() => {
          status.hidden = true;
          status.textContent = '';
        }, 400);
      }
    }, 2000);
  } catch (error) {
    console.error('Gift note update failed', error);
    button.textContent = 'Error - Try Again';
    button.disabled = false;
    setTimeout(() => {
      button.textContent = originalText;
    }, 2000);
  }
};

/**
 * Zoom on click: open the gift wrap image in the <dialog> that snippets/wrapin.liquid
 * renders next to #ptw-wrapin when zoom_on_click is on.
 */
const openZoom = (image) => {
  const wrapper = image.closest('#ptw-wrapin');
  const dialog = wrapper.parentElement.querySelector('[data-ptw-zoom-dialog]');
  if (typeof dialog?.showModal !== 'function') {
    return false;
  }
  const zoomImage = dialog.querySelector('.ptw-zoom-dialog__image');
  const src = wrapper.dataset.zoomImage;
  if (zoomImage.getAttribute('src') !== src) {
    zoomImage.src = src;
  }
  if (!dialog.open) {
    dialog.showModal();
  }
  return true;
};

document.addEventListener('click', (event) => {
  const { target } = event;

  const addButton = target.closest(`#${ADD_BUTTON_ID}`);
  if (addButton) {
    event.preventDefault();
    addGiftWrap(addButton);
    return;
  }

  const noteButton = target.closest('#giftNoteCta');
  if (noteButton) {
    event.preventDefault();
    event.stopPropagation();
    saveGiftNote(noteButton);
    return;
  }

  const zoomTrigger = target.closest('#ptw-wrapin[data-zoom-image] .ptw-gift-image');
  if (zoomTrigger && openZoom(zoomTrigger)) {
    // The image sits inside <label for="ptw-gift-needed">; don't toggle the checkbox.
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  // Close on the close button, or on a backdrop click (the target is the dialog itself).
  const dialog = target.closest('[data-ptw-zoom-dialog]');
  if (dialog && (target === dialog || target.closest('[data-ptw-zoom-close]'))) {
    dialog.close();
  }
});

document.addEventListener('theme:cart:change', scheduleButtonSync);

// Modules are deferred. If the app injected its HTML before this ran, it could not call the hook,
// so run it now. The hook is safe to run twice.
if (document.querySelector('#ptw-wrapin .ptw-app-wrapper')) {
  window.ptwAfterWrapinLoadHook();
}

// Legacy clean-up: move a note stored in the gift-note attribute into the cart note,
// and clear the attribute.
window.addEventListener('pageshow', async () => {
  try {
    const cart = await cartRequest('cart.js');
    const legacyNote = cart.attributes?.['gift-note'];
    if (!legacyNote) {
      return;
    }
    const note = cart.note || legacyNote.replace(/^gift-note\s*\n?/i, '').trim();
    await updateCartNote(note);
  } catch (error) {
    console.warn('Gift note resync failed', error);
  }
});
